<template>
  <Button
    ref="trigger"
    :variant="buttonVariant"
    :icon="triggerIconPrefix"
    :active="showDropdown"
    :icon-only="!triggerText"
    type="button"
    @click.prevent.stop="toggleDropdown"
  >
    <template v-if="triggerIconPrefix && triggerText" #prefix>
      <Icon :src="triggerIconPrefix" />
    </template>
    <Icon v-if="triggerIconPrefix && !triggerText" :src="triggerIconPrefix" />
    {{ triggerText }}
    <template v-if="triggerIconSuffix" #suffix>
      <Icon :src="triggerIconSuffix" />
    </template>
  </Button>

  <DropdownMenu
    v-if="trigger"
    ref="dropdown"
    v-model:open="showDropdown"
    :toggle-element="trigger.el"
    :overlay="false"
    :calculate-position="calculatePosition"
    :max-width="maxWidth"
    :min-width="minWidth"
  >
    <template v-if="filterable">
      <DropdownItemSearch
        ref="searchElm"
        v-model="search"
        @keydown.down.prevent="() => setFocusOnItem(-1, 'down')"
        @keydown.up.prevent="() => setFocusOnItem(-1, 'up')"
      />
      <Divider :spacing="false" />
    </template>
    <div
      ref="optionsWrapper"
      class="overflow-y-auto p-1.5"
      :style="{
        'max-height': maxHeight,
      }"
    >
      <div v-if="customOption">
        <DropdownItem
          :title="customOptionLabel"
          :variant="'default'"
          @click="() => selectOption('new')"
          @keydown.enter.prevent="() => selectOption('new')"
        >
          <template #prefix>
            <Icon src="plus" />
          </template>
        </DropdownItem>
      </div>
      <template
        v-for="(option, index) in filteredOptions"
        :key="`${search}_${option ? option.value : 'divider_' + index}`"
      >
        <Divider v-if="option == null" />

        <DropdownItem
          v-else
          :has-arrow="hasArrow"
          :title="option.label"
          :subtext="option.subtext"
          :disabled="option.disabled"
          :variant="option.destructive ? 'destructive' : 'default'"
          @click="() => selectOption(option.value)"
          @keydown.enter.prevent="() => selectOption(option.value)"
          @keydown.down.prevent="() => setFocusOnItem(index, 'down')"
          @keydown.up.prevent="() => setFocusOnItem(index, 'up')"
        >
          <template v-if="option.icon" #prefix>
            <Icon :src="option.icon" />
          </template>
          <template v-else-if="option.image" #prefix>
            <Avatar
              :size="ComponentSize.sm"
              :entity="{
                image: option.image,
              }"
            />
          </template>
          <template #suffix>
            <Icon v-if="option.suffixIcon" :src="option.suffixIcon" />
          </template>
        </DropdownItem>
      </template>
    </div>
  </DropdownMenu>
</template>

<script setup lang="ts">
  const {
    actions,
    buttonVariant = ButtonVariant.Tertiary,
    centerHorizontally = false,
    filterable = false,
    hasArrow = false,
    maxHeight = "16rem",
    maxWidth = undefined,
    minWidth = undefined,
    triggerIconPrefix = null,
    triggerIconSuffix = null,
    triggerText = undefined,
    customOption = false,
    customOptionLabel = "Add new",
  } = defineProps<{
    actions: (DropdownOption | null)[];
    buttonVariant?: ButtonVariant;
    // centre the list under the trigger, e.g. for a full-width "Add …" button
    centerHorizontally?: boolean;
    filterable?: boolean;
    hasArrow?: boolean;
    maxHeight?: string;
    maxWidth?: string;
    minWidth?: string;
    triggerIconPrefix?: string | null;
    triggerIconSuffix?: string | null;
    triggerText?: string;
    customOption?: boolean;
    customOptionLabel?: string;
  }>();

  const { dropdown, trigger, showDropdown, toggleDropdown, calculatePosition } = useDropdown({
    repositionFromBottom: true,
    offset: { top: 4, left: 0 },
    centerHorizontally,
  });
  const modelValue = ref("");
  const emit = defineEmits(["select", "update:modelValue"]);
  const options = computed(() => actions);
  const { searchElm, optionsWrapper, search, filteredOptions, setFocusOnItem, selectOption } = useSelect({
    options,
    modelValue,
    emit,
    open: showDropdown,
    toggleDropdown,
  });
</script>
